import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import DashboardMetricValue from './DashboardMetricValue';
import DashboardWindowToggle from './DashboardWindowToggle';
import SwitchField from '../common/SwitchField';
import useDashboardMetric from '../../hooks/useDashboardMetric';
import dashboardService from '../../services/dashboardService';
import {
  formatNumber,
  formatPercent,
  NEW_CUSTOMER_WINDOW_DAYS,
  RETURN_RATE_WINDOW_DAYS,
} from '../../utils/dashboardMetricUtils';

const useCardAttempt = () => {
  const [attempt, setAttempt] = useState(0);
  return { attempt, retry: () => setAttempt((current) => current + 1) };
};

export const NewCustomersCard = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(NEW_CUSTOMER_WINDOW_DAYS[1]);
  const { attempt, retry } = useCardAttempt();
  const metric = useDashboardMetric({
    cacheKey: `dashboard:new-customers:${storeId}:${days}`,
    fetcher: () => dashboardService.getDashboardNewCustomers(storeId, { days }),
    enabled: Boolean(storeId),
    revision: attempt,
  });

  if (metric.forbidden) return null;

  return (
    <DashboardCard
      id="dashboard-new-customers"
      title={t('dashboard.metrics.newCustomers')}
      subtitle={t('dashboard.metrics.newCustomersSubtitle', { days })}
      isLoading={metric.loading && !metric.data}
      hasError={Boolean(metric.error)}
      onRetry={retry}
    >
      <DashboardMetricValue value={formatNumber(metric.data?.count ?? 0, i18n.language, 0)} />
      <DashboardWindowToggle
        options={NEW_CUSTOMER_WINDOW_DAYS}
        value={days}
        onChange={setDays}
        getLabel={(option) => t('dashboard.rates.window', { days: option })}
        ariaLabel={t('dashboard.metrics.newCustomers')}
      />
    </DashboardCard>
  );
};

export const ReturnRateCard = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(RETURN_RATE_WINDOW_DAYS[0]);
  const [ignoreInactive, setIgnoreInactive] = useState(false);
  const { attempt, retry } = useCardAttempt();
  const metric = useDashboardMetric({
    cacheKey: `dashboard:return-rate:${storeId}:${days}:${ignoreInactive}`,
    fetcher: () => dashboardService.getDashboardReturnRate(storeId, { days, ignoreInactive }),
    enabled: Boolean(storeId),
    revision: attempt,
  });

  if (metric.forbidden) return null;

  const data = metric.data;

  return (
    <DashboardCard
      id="dashboard-return-rate"
      title={t('dashboard.metrics.returnRate')}
      subtitle={t('dashboard.metrics.returnRateSubtitle')}
      isLoading={metric.loading && !metric.data}
      hasError={Boolean(metric.error)}
      onRetry={retry}
    >
      <DashboardMetricValue
        value={formatPercent(data?.percent, i18n.language)}
        caption={data ? t('dashboard.metrics.ofCustomers', { returned: data.returnedCount, total: data.baseCount }) : null}
      />
      <DashboardWindowToggle
        options={RETURN_RATE_WINDOW_DAYS}
        value={days}
        onChange={setDays}
        getLabel={(option) => t('dashboard.rates.window', { days: option })}
        ariaLabel={t('dashboard.metrics.returnRate')}
      />
      <SwitchField
        id="dashboard-return-rate-ignore-inactive"
        compact
        checked={ignoreInactive}
        onChange={(event) => setIgnoreInactive(event.target.checked)}
        label={t('dashboard.rates.ignoreInactive')}
        helpText={t('dashboard.rates.ignoreInactiveHelp')}
      />
    </DashboardCard>
  );
};

export const NewCustomerReturnRateCard = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(RETURN_RATE_WINDOW_DAYS[0]);
  const { attempt, retry } = useCardAttempt();
  const metric = useDashboardMetric({
    cacheKey: `dashboard:new-customer-return-rate:${storeId}:${days}`,
    fetcher: () => dashboardService.getDashboardNewCustomerReturnRate(storeId, { days }),
    enabled: Boolean(storeId),
    revision: attempt,
  });

  if (metric.forbidden) return null;

  const data = metric.data;

  return (
    <DashboardCard
      id="dashboard-new-customer-return-rate"
      title={t('dashboard.metrics.newCustomerReturnRate')}
      subtitle={t('dashboard.metrics.newCustomerReturnRateSubtitle')}
      isLoading={metric.loading && !metric.data}
      hasError={Boolean(metric.error)}
      onRetry={retry}
    >
      <DashboardMetricValue
        value={formatPercent(data?.percent, i18n.language)}
        caption={data ? t('dashboard.metrics.ofNewCustomers', { repeat: data.repeatCount, total: data.newCount }) : null}
      />
      <DashboardWindowToggle
        options={RETURN_RATE_WINDOW_DAYS}
        value={days}
        onChange={setDays}
        getLabel={(option) => t('dashboard.rates.window', { days: option })}
        ariaLabel={t('dashboard.metrics.newCustomerReturnRate')}
      />
    </DashboardCard>
  );
};
